import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FilePenLine } from 'lucide-react'
import { useActiveSpecialLecture } from '@/hooks/learner/useActiveSpecialLecture'
import { TestLayout } from '@/components/learner/test'
import { SpecialLectureStartCard } from './SpecialLectureStartCard'
import { SpecialLectureLoading, SpecialLecturePageState } from './SpecialLecturePageState'

/** 프린트 단원평가 특강 대기 화면 — 종이 응시/채점은 오프라인, 리뷰 오픈 시 자동 전환. */
export default function SpecialLectureWait() {
  useParams<{ id: string }>()
  const { t } = useTranslation('learner')
  // active 폴링으로 리뷰 오픈 시 flow hook 이 리뷰로 redirect(공유 query 캐시 갱신).
  const active = useActiveSpecialLecture({ pollInterval: 10_000 })
  const lock = active.data
  const isPrintInProgress = lock?.mode === 'PRINT'

  if (active.isLoading) {
    return <SpecialLectureLoading />
  }

  if (active.isError) {
    return (
      <SpecialLecturePageState
        kind="error"
        title={t('specialLecture.state.errorTitle')}
        description={t('specialLecture.error.load')}
        actionLabel={t('specialLecture.state.retry')}
        onAction={() => { void active.refetch() }}
      />
    )
  }

  if (!lock) {
    return (
      <SpecialLecturePageState
        kind="empty"
        title={t('specialLecture.state.noActiveTitle')}
        description={t('specialLecture.state.noActiveDescription')}
      />
    )
  }

  if (isPrintInProgress) {
    return (
      <SpecialLectureStartCard
        setName={lock.setName}
        title={t('specialLecture.start.title')}
        description={t('specialLecture.wait.printDesc')}
        count={lock.itemCount}
        countLabel={lock.itemCount
          ? t('specialLecture.start.count', { total: lock.itemCount })
          : undefined}
      />
    )
  }

  return (
    <TestLayout variant="card">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex size-20 items-center justify-center rounded-full bg-lc-key-50 text-lc-key-500">
          <FilePenLine className="size-12" strokeWidth={1.8} aria-hidden />
        </div>
        <h1 className="text-lc-v3-h3 font-bold text-lc-text-primary sm:text-lc-v3-h1">
          {t('specialLecture.wait.reviewTitle')}
        </h1>
        <p className="whitespace-pre-line text-lc-v3-body-1 font-medium text-lc-text-secondary">
          {t('specialLecture.wait.reviewDesc')}
        </p>
      </div>
    </TestLayout>
  )
}
